import { githubAuthApiRef } from "@backstage/core-plugin-api";
import { Octokit } from "octokit";

import { K8sResource } from "../types";

type GithubAuthApi = typeof githubAuthApiRef.T;

interface GitHubRelease {
    tag_name: string;
    name: string;
    published_at: string;
}

export interface VersionInfo {
    latestRelease: string;
    versionsBehind: number;
    error?: string;
}

interface CacheEntry {
    releases: GitHubRelease[];
    fetchedAt: number;
    lastAccessed: number;
}

const CACHE_TTL = 10 * 60 * 1000;
const MAX_CACHE_SIZE = 100;
const releasesCache = new Map<string, CacheEntry>();

const NEGATIVE_CACHE_TTL = 60 * 60 * 1000;
const notFoundRepos = new Map<string, number>();

function cleanupCache() {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    for (const [key, entry] of releasesCache.entries()) {
        if (now - entry.fetchedAt > CACHE_TTL) {
            entriesToDelete.push(key);
        }
    }

    entriesToDelete.forEach(key => releasesCache.delete(key));

    if (releasesCache.size > MAX_CACHE_SIZE) {
        const sortedEntries = Array.from(releasesCache.entries()).sort(
            (a, b) => a[1].lastAccessed - b[1].lastAccessed
        );

        const toRemove = sortedEntries.slice(
            0,
            releasesCache.size - MAX_CACHE_SIZE
        );
        toRemove.forEach(([key]) => releasesCache.delete(key));
    }

    for (const [repo, timestamp] of notFoundRepos.entries()) {
        if (now - timestamp > NEGATIVE_CACHE_TTL) {
            notFoundRepos.delete(repo);
        }
    }
}

function extractAppName(deployment: K8sResource): string | null {
    const annotations = deployment?.metadata?.annotations ?? {};
    const labels = deployment?.metadata?.labels ?? {};

    const labelSelector = annotations["backstage.io/kubernetes-label-selector"];
    if (labelSelector && typeof labelSelector === "string") {
        const match = labelSelector.match(/app=([^\s,]+)/);
        if (match) {
            return match[1];
        }
    }

    const kubernetesId = annotations["backstage.io/kubernetes-id"];
    if (kubernetesId && typeof kubernetesId === "string") {
        return kubernetesId;
    }

    if (labels["app.kubernetes.io/name"]) {
        return labels["app.kubernetes.io/name"];
    }
    if (labels["app"]) {
        return labels["app"];
    }

    return null;
}

export function extractGitHubRepo(
    deployment: K8sResource,
    githubOrg?: string
): string | null {
    const annotations = deployment?.metadata?.annotations ?? {};

    const repoPatterns = [
        annotations["github.com/repo"],
        annotations["github.com/project-slug"],
        annotations["backstage.io/source-location"],
    ];

    for (const pattern of repoPatterns) {
        if (pattern && typeof pattern === "string") {
            const match = pattern.match(
                /^(?:https?:\/\/)?(?:www\.)?github\.com\/([^/]+\/[^/]+)/
            );
            if (match) {
                return match[1].replace(/\.git$/, "");
            }
            if (
                pattern.includes("/") &&
                !pattern.includes(":") &&
                !pattern.includes("//")
            ) {
                return pattern;
            }
        }
    }

    const appName = extractAppName(deployment);

    // Fall back to the org's configured GitHub org (from
    // `k8sDashboard.organizations[].githubOrg`) when the deployment has no
    // explicit repo annotation.
    if (appName && githubOrg) {
        return `${githubOrg}/${appName}`;
    }

    return null;
}

export async function fetchGitHubReleases(
    repoSlug: string,
    githubAuth: GithubAuthApi
): Promise<GitHubRelease[]> {
    cleanupCache();

    const notFoundTimestamp = notFoundRepos.get(repoSlug);
    if (notFoundTimestamp) {
        const now = Date.now();
        if (now - notFoundTimestamp < NEGATIVE_CACHE_TTL) {
            return [];
        }
    }

    const cached = releasesCache.get(repoSlug);
    if (cached) {
        const now = Date.now();
        if (now - cached.fetchedAt < CACHE_TTL) {
            cached.lastAccessed = now;
            return cached.releases;
        }
    }

    const [owner, repo] = repoSlug.split("/");
    const token = await githubAuth.getAccessToken(["repo"]);
    const octokit = new Octokit({ auth: token });

    try {
        await octokit.rest.repos.get({ owner, repo });
    } catch (error: unknown) {
        if ((error as { status?: number }).status === 404) {
            notFoundRepos.set(repoSlug, Date.now());
        }
        return [];
    }

    const [releasesResponse, tagsResponse] = await Promise.all([
        octokit.rest.repos
            .listReleases({
                owner,
                repo,
                per_page: 100,
            })
            .catch((error: unknown) => {
                if ((error as { status?: number }).status === 404) {
                    notFoundRepos.set(repoSlug, Date.now());
                }
                return { data: [] };
            }),
        octokit.rest.repos
            .listTags({
                owner,
                repo,
                per_page: 100,
            })
            .catch((error: unknown) => {
                if ((error as { status?: number }).status === 404) {
                    notFoundRepos.set(repoSlug, Date.now());
                }
                return { data: [] };
            }),
    ]);

    const releases: GitHubRelease[] = releasesResponse.data.map(release => ({
        tag_name: release.tag_name,
        name: release.name ?? release.tag_name,
        published_at: release.published_at ?? "",
    }));

    const releaseTags = new Set(releases.map(r => r.tag_name));

    const additionalTags: GitHubRelease[] = tagsResponse.data
        .filter(tag => !releaseTags.has(tag.name))
        .map(tag => ({
            tag_name: tag.name,
            name: tag.name,
            published_at: "",
        }));

    const allReleases = [...releases, ...additionalTags];

    const now = Date.now();
    releasesCache.set(repoSlug, {
        releases: allReleases,
        fetchedAt: now,
        lastAccessed: now,
    });

    return allReleases;
}

export function extractVersionFromResource(
    resource: K8sResource
): string | null {
    const versionLabelNames = [
        "version",
        "app.kubernetes.io/version",
        "app.version",
        "Version",
    ];

    for (const labelName of versionLabelNames) {
        const version = resource?.metadata?.labels?.[labelName];
        if (version && version !== "N/A") {
            return version;
        }
    }

    const containerImage =
        resource?.spec?.template?.spec?.containers?.[0]?.image ||
        resource?.spec?.containers?.[0]?.image;
    if (containerImage) {
        const tag = containerImage.split(":").pop();
        if (tag && tag !== "latest" && tag !== "N/A") {
            return tag;
        }
    }

    return null;
}

function extractSemanticVersion(versionString: string): string | null {
    const match = versionString.match(/v?(\d+\.\d+\.\d+)/);
    return match ? match[1] : null;
}

export function calculateVersionsBehind(
    deployedVersion: string,
    releases: GitHubRelease[]
): VersionInfo {
    if (!releases || releases.length === 0) {
        return {
            latestRelease: "N/A",
            versionsBehind: -1,
            error: "No releases found",
        };
    }

    const latestRelease = releases[0].tag_name;
    const deployedSemver = extractSemanticVersion(deployedVersion);
    const latestSemver = extractSemanticVersion(latestRelease);

    if (!deployedSemver) {
        return {
            latestRelease,
            versionsBehind: -1,
            error: "Could not parse deployed version as semantic version",
        };
    }

    if (!latestSemver) {
        return {
            latestRelease,
            versionsBehind: -1,
            error: "Could not parse latest release as semantic version",
        };
    }

    if (deployedSemver === latestSemver) {
        return {
            latestRelease,
            versionsBehind: 0,
        };
    }

    const deployedIndex = releases.findIndex(release => {
        const releaseSemver = extractSemanticVersion(release.tag_name);
        return releaseSemver === deployedSemver;
    });

    if (deployedIndex === -1) {
        return {
            latestRelease,
            versionsBehind: -1,
            error: "Deployed version not found in releases",
        };
    }

    return {
        latestRelease,
        versionsBehind: deployedIndex,
    };
}

export async function getVersionInfo(
    deployment: K8sResource,
    githubAuth: GithubAuthApi,
    githubOrg?: string
): Promise<VersionInfo | null> {
    const repoSlug = extractGitHubRepo(deployment, githubOrg);
    if (!repoSlug) {
        return null;
    }

    const deployedVersion = extractVersionFromResource(deployment);

    if (!deployedVersion) {
        return null;
    }

    try {
        const releases = await fetchGitHubReleases(repoSlug, githubAuth);
        return calculateVersionsBehind(deployedVersion, releases);
    } catch {
        return {
            latestRelease: "Error",
            versionsBehind: -1,
            error: "Failed to fetch releases",
        };
    }
}
